import React, { useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ThumbDownOutlinedIcon from '@material-ui/icons/ThumbDownOutlined';
import Notification from '@this/src/notification';
import type { FeedbackType } from '../../types/messages';
import { updateAiMessageFeedback } from '../../api/messages';

type Props = {
  messageId: string;
  tripId: string;
  currentFeedback: FeedbackType | null;
  onFeedbackUpdate: (messageId: string, feedback: FeedbackType) => void;
};

const FEEDBACK_TYPES = {
  good: 'good',
  bad: 'bad'
} as const;

const FEEDBACK_OPTIONS: Record<
  FeedbackType,
  {
    buttonLabel: string;
    selectedLabel: string;
    filledIcon: typeof ThumbUpIcon;
    outlinedIcon: typeof ThumbUpOutlinedIcon;
  }
> = {
  [FEEDBACK_TYPES.good]: {
    buttonLabel: 'good',
    selectedLabel: 'good-selected',
    filledIcon: ThumbUpIcon,
    outlinedIcon: ThumbUpOutlinedIcon
  },
  [FEEDBACK_TYPES.bad]: {
    buttonLabel: 'bad',
    selectedLabel: 'bad-selected',
    filledIcon: ThumbDownIcon,
    outlinedIcon: ThumbDownOutlinedIcon
  }
};

const AiMessageFeedback = ({ messageId, tripId, currentFeedback, onFeedbackUpdate }: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const styles = getStyles();

  if (currentFeedback !== null) {
    const { filledIcon: filledIconComponent, selectedLabel } = FEEDBACK_OPTIONS[currentFeedback];
    return (
      <div style={styles.container}>
        <div aria-label={selectedLabel} style={styles.selectedIcon}>
          {React.createElement(filledIconComponent, { style: styles.icon })}
        </div>
      </div>
    );
  }

  const handleFeedback = async (feedbackType: FeedbackType) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await updateAiMessageFeedback(tripId, messageId, feedbackType);
      onFeedbackUpdate(messageId, feedbackType);
      Notification.success('フィードバックありがとうございます');
    } catch {
      Notification.error('フィードバックの送信に失敗しました。時間をおいてもう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={styles.container}>
      {(Object.values(FEEDBACK_TYPES) as FeedbackType[]).map(feedbackType => {
        const { buttonLabel, outlinedIcon: outlinedIconComponent } = FEEDBACK_OPTIONS[feedbackType];
        return (
          <IconButton
            key={feedbackType}
            aria-label={buttonLabel}
            size="small"
            disabled={isSubmitting}
            onClick={() => handleFeedback(feedbackType)}
          >
            {React.createElement(outlinedIconComponent, { style: styles.icon })}
          </IconButton>
        );
      })}
    </div>
  );
};

const getStyles = () =>
  ({
    container: {
      display: 'flex',
      gap: '4px',
      marginTop: '4px'
    },
    selectedIcon: {
      color: '#111111',
      display: 'flex',
      alignItems: 'center'
    },
    icon: {
      fontSize: '16px'
    }
  } as const);

export default AiMessageFeedback;
